import { Suspense, lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminLayout, AppLayout, AuthLayout, CompanyLayout, InterviewLayout, LandingLayout, PublicLayout, SettingsLayout } from './layouts/index.js';
import { RequireAdmin } from './components/RequireAdmin.js';
import { RequireAuth } from './components/RequireAuth.js';
import { RouteErrorPage } from './components/RouteErrorPage.js';
import { AppLoader } from './components/shared/AppLoader.js';
// Eager: only the small auth/error entry pages — everything else is lazy so
// the initial bundle stays small (admin, interview/Monaco, the GSAP-heavy
// landing all load on demand as separate chunks).
import { LoginPage } from './pages/LoginPage.js';
import { RegisterPage } from './pages/RegisterPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';
import { AuthActionPage } from './pages/AuthActionPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { ServerErrorPage } from './pages/ServerErrorPage.js';

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-ig-bg-primary">
      <AppLoader size="sm" className="min-h-0" />
    </div>
  );
}

/**
 * Wraps a named export from a lazily imported page module in Suspense.
 * Direct module paths (never the pages barrel) so Vite emits one chunk per
 * page instead of one chunk for everything.
 */
function lazyPage<M extends Record<string, unknown>, K extends keyof M>(
  loader: () => Promise<M>,
  name: K,
  props?: Record<string, unknown>,
) {
  const Component = lazy(async () => ({ default: (await loader())[name] as ComponentType }));
  const Rendered = Component as ComponentType<Record<string, unknown>>;
  return (
    <Suspense fallback={<PageFallback />}>
      <Rendered {...(props ?? {})} />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    // Pathless boundary route: any render/loader error inside ANY route lands
    // on RouteErrorPage instead of white-screening the app.
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '/',
        element: <LandingLayout />,
        children: [
          { index: true, element: lazyPage(() => import('./pages/LandingPage.js'), 'LandingPage') },
          { path: 'how-it-works', element: lazyPage(() => import('./pages/landing/LandingFocusPages.js'), 'HowItWorksPage') },
          { path: 'features', element: lazyPage(() => import('./pages/landing/LandingFocusPages.js'), 'FeaturesPage') },
          { path: 'pricing', element: lazyPage(() => import('./pages/landing/LandingFocusPages.js'), 'PricingPage') },
          { path: 'for-companies', element: lazyPage(() => import('./pages/landing/CompanyPages.js'), 'ForCompaniesPage') },
          { path: 'pricing/companies', element: lazyPage(() => import('./pages/landing/CompanyPages.js'), 'CompanyPricingPage') },
          { path: 'faq', element: lazyPage(() => import('./pages/landing/LandingFocusPages.js'), 'FaqPage') },
          { path: 'about', element: lazyPage(() => import('./pages/landing/AboutPage.js'), 'AboutPage') },
          { path: 'sample-report', element: lazyPage(() => import('./pages/SampleReportPage.js'), 'SampleReportPage') },
        ],
      },
      ...(['', 'en/', 'pl/'] as const).flatMap((prefix) => [
        {
          path: `/${prefix}privacy`,
          element: <PublicLayout />,
          children: [{ index: true, element: lazyPage(() => import('./pages/legal/PrivacyPolicy.js'), 'PrivacyPolicy', prefix ? { forcedLanguage: prefix.replace('/', '') } : undefined) }],
        },
        {
          path: `/${prefix}terms`,
          element: <PublicLayout />,
          children: [{ index: true, element: lazyPage(() => import('./pages/legal/TermsOfService.js'), 'TermsOfService', prefix ? { forcedLanguage: prefix.replace('/', '') } : undefined) }],
        },
        {
          path: `/${prefix}cookies`,
          element: <PublicLayout />,
          children: [{ index: true, element: lazyPage(() => import('./pages/legal/CookiePolicy.js'), 'CookiePolicy', prefix ? { forcedLanguage: prefix.replace('/', '') } : undefined) }],
        },
        {
          path: `/${prefix}support`,
          element: <PublicLayout />,
          children: [{ index: true, element: lazyPage(() => import('./pages/SupportPage.js'), 'SupportPage') }],
        },
        {
          path: `/${prefix}tutorials`,
          element: <PublicLayout />,
          children: [{ index: true, element: lazyPage(() => import('./pages/TutorialsPage.js'), 'TutorialsPage') }],
        },
      ]),
      {
        // Firebase email action handler (verification links, password reset).
        // Must live outside AuthLayout — the link is opened from email, often
        // while signed out, and must run before any auth redirects.
        path: '/__/auth/action',
        element: <AuthActionPage />,
      },
      {
        path: '/login',
        element: <AuthLayout />,
        children: [{ index: true, element: <LoginPage /> }],
      },
      {
        path: '/register',
        element: <AuthLayout />,
        children: [{ index: true, element: <RegisterPage /> }],
      },
      {
        // Post-registration email activation gate (password sign-ups only).
        // Lives outside RequireAuth so blocked accounts can reach it; the page
        // itself redirects to /login when signed out and into the app once
        // verified.
        path: '/verify-email',
        element: <AuthLayout />,
        children: [{ index: true, element: <VerifyEmailPage /> }],
      },
      {
        path: '/unsubscribe/:token',
        element: lazyPage(() => import('./pages/UnsubscribePage.js'), 'UnsubscribePage'),
      },
      {
        path: '/invite/:token',
        element: lazyPage(() => import('./pages/InvitePage.js'), 'InvitePage'),
      },
      {
        path: '/500',
        element: <ServerErrorPage />,
      },
      {
        element: <RequireAuth />,
        children: [
          // Standalone (own full-screen chrome): post-registration account-type wizard.
          { path: '/welcome', element: lazyPage(() => import('./pages/WelcomePage.js'), 'WelcomePage') },
          {
            element: <AppLayout />,
            children: [
              { path: '/dashboard', element: lazyPage(() => import('./pages/DashboardPage.js'), 'DashboardPage') },
              { path: '/interview/setup', element: lazyPage(() => import('./pages/SetupPage.js'), 'SetupPage') },
              {
                path: '/settings',
                element: <SettingsLayout />,
                children: [
                  { index: true, element: lazyPage(() => import('./pages/settings/SettingsIndexRedirect.js'), 'SettingsIndexRedirect') },
                  { path: 'profile', element: lazyPage(() => import('./pages/settings/ProfilePage.js'), 'ProfilePage') },
                  { path: 'history', element: <Navigate to="/dashboard" replace /> },
                  { path: 'plan', element: lazyPage(() => import('./pages/settings/PackPage.js'), 'PackPage') },
                  { path: 'pack', element: <Navigate to="/settings/plan" replace /> },
                  { path: 'billing', element: lazyPage(() => import('./pages/settings/BillingPage.js'), 'BillingPage') },
                  { path: 'promo-codes', element: lazyPage(() => import('./pages/settings/PromoCodesPage.js'), 'PromoCodesPage') },
                  { path: 'security', element: lazyPage(() => import('./pages/settings/SecurityPage.js'), 'SecurityPage') },
                  { path: 'privacy', element: lazyPage(() => import('./pages/settings/PrivacyPage.js'), 'PrivacyPage') },
                  { path: 'notifications', element: lazyPage(() => import('./pages/settings/NotificationsPage.js'), 'NotificationsPage') },
                  { path: 'preferences', element: lazyPage(() => import('./pages/settings/PreferencesPage.js'), 'PreferencesPage') },
                ],
              },
              { path: '/payment', element: lazyPage(() => import('./pages/PaymentPage.js'), 'PaymentPage') },
              {
                path: '/company',
                element: <CompanyLayout />,
                children: [
                  { index: true, element: lazyPage(() => import('./pages/company/CompanyDashboardPage.js'), 'CompanyDashboardPage') },
                  { path: 'simulations', element: lazyPage(() => import('./pages/company/CompanySimulationsPage.js'), 'CompanySimulationsPage') },
                  { path: 'simulations/new', element: lazyPage(() => import('./pages/company/TemplateWizardPage.js'), 'TemplateWizardPage') },
                  { path: 'simulations/:templateId/edit', element: lazyPage(() => import('./pages/company/TemplateWizardPage.js'), 'TemplateWizardPage') },
                  { path: 'candidates/:simulationId', element: lazyPage(() => import('./pages/company/CompanyCandidateReportPage.js'), 'CompanyCandidateReportPage') },
                  { path: 'settings', element: lazyPage(() => import('./pages/company/CompanySettingsPage.js'), 'CompanySettingsPage') },
                ],
              },
              { path: '/interview/:id/stage/:stageNumber/feedback', element: lazyPage(() => import('./pages/FeedbackPage.js'), 'FeedbackPage') },
              { path: '/interview/:id/feedback-language', element: lazyPage(() => import('./pages/LanguageFeedbackPage.js'), 'LanguageFeedbackPage') },
              { path: '/interview/:id/report', element: lazyPage(() => import('./pages/FinalReportPage.js'), 'FinalReportPage') },
            ],
          },
          {
            element: <InterviewLayout />,
            children: [
              { path: '/interview/:id/language-check', element: lazyPage(() => import('./pages/LanguageCheckPage.js'), 'LanguageCheckPage') },
              { path: '/interview/:id/stage/:stageNumber', element: lazyPage(() => import('./pages/ActiveInterviewPage.js'), 'ActiveInterviewPage') },
              // EXPERIMENTAL one-call interview — same engines, dedicated URL space.
              { path: '/interview/:id/call', element: lazyPage(() => import('./pages/CallEntryPage.js'), 'CallEntryPage') },
              { path: '/interview/:id/call/language', element: lazyPage(() => import('./pages/LanguageCheckPage.js'), 'LanguageCheckPage') },
              { path: '/interview/:id/call/:stageNumber', element: lazyPage(() => import('./pages/ActiveInterviewPage.js'), 'ActiveInterviewPage') },
            ],
          },
          {
            element: <RequireAdmin />,
            children: [
              {
                element: <AdminLayout />,
                children: [
                  { path: '/admin', element: lazyPage(() => import('./pages/admin/AdminDashboardPage.js'), 'AdminDashboardPage') },
                  { path: '/admin/users', element: lazyPage(() => import('./pages/admin/UsersPage.js'), 'UsersPage') },
                  { path: '/admin/users/:id', element: lazyPage(() => import('./pages/admin/UserDetailPage.js'), 'UserDetailPage') },
                  { path: '/admin/simulations', element: lazyPage(() => import('./pages/admin/SimulationsPage.js'), 'SimulationsPage') },
                  { path: '/admin/simulations/:id', element: lazyPage(() => import('./pages/admin/SimulationDetailPage.js'), 'SimulationDetailPage') },
                  { path: '/admin/payments', element: lazyPage(() => import('./pages/admin/PaymentsPage.js'), 'PaymentsPage') },
                  { path: '/admin/analytics', element: lazyPage(() => import('./pages/admin/AnalyticsPage.js'), 'AnalyticsPage') },
                  { path: '/admin/logs', element: lazyPage(() => import('./pages/admin/LogsPage.js'), 'LogsPage') },
                  { path: '/admin/prompts', element: lazyPage(() => import('./pages/admin/PromptsPage.js'), 'PromptsPage') },
                  { path: '/admin/prompts/:key', element: lazyPage(() => import('./pages/admin/PromptEditorPage.js'), 'PromptEditorPage') },
                  { path: '/admin/test-lab', element: lazyPage(() => import('./pages/admin/TestLabPage.js'), 'TestLabPage') },
                  { path: '/admin/test-lab/history', element: lazyPage(() => import('./pages/admin/TestRunHistoryPage.js'), 'TestRunHistoryPage') },
                  { path: '/admin/interview-settings', element: lazyPage(() => import('./pages/admin/InterviewSettingsPage.js'), 'InterviewSettingsPage') },
                  { path: '/admin/promo-codes', element: lazyPage(() => import('./pages/admin/PromoCodesPage.js'), 'PromoCodesPage') },
                  { path: '/admin/promo-codes/:id', element: lazyPage(() => import('./pages/admin/PromoCodeDetailPage.js'), 'PromoCodeDetailPage') },
                  { path: '/admin/plans', element: lazyPage(() => import('./pages/admin/PlanDefinitionsPage.js'), 'PlanDefinitionsPage') },
                  { path: '/admin/promotions', element: lazyPage(() => import('./pages/admin/PromotionsPage.js'), 'PromotionsPage') },
                  { path: '/admin/campaigns', element: lazyPage(() => import('./pages/admin/CampaignsPage.js'), 'CampaignsPage') },
                  { path: '/admin/testers', element: lazyPage(() => import('./pages/admin/TestersPage.js'), 'TestersPage') },
                  { path: '/admin/maintenance', element: lazyPage(() => import('./pages/admin/MaintenancePage.js'), 'MaintenancePage') },
                  { path: '/admin/discount-codes', element: lazyPage(() => import('./pages/admin/DiscountCodesPage.js'), 'DiscountCodesPage') },
                  { path: '/admin/outreach', element: lazyPage(() => import('./pages/admin/OutreachPage.js'), 'OutreachPage') },
                  { path: '/admin/support', element: lazyPage(() => import('./pages/admin/SupportMessagesPage.js'), 'SupportMessagesPage') },
                  { path: '/admin/support/:id', element: lazyPage(() => import('./pages/admin/SupportMessageDetailPage.js'), 'SupportMessageDetailPage') },
                  { path: '/admin/newsletters', element: lazyPage(() => import('./pages/admin/NewsletterDashboardPage.js'), 'NewsletterDashboardPage') },
                  { path: '/admin/newsletter/compose', element: lazyPage(() => import('./pages/admin/NewsletterComposePage.js'), 'NewsletterComposePage') },
                  { path: '/admin/newsletters/campaign/:id', element: lazyPage(() => import('./pages/admin/NewsletterDetailPage.js'), 'NewsletterDetailPage') },
                  { path: '/admin/newsletters/subscribers', element: lazyPage(() => import('./pages/admin/NewsletterSubscribersPage.js'), 'NewsletterSubscribersPage') },
                  { path: '/admin/email-test', element: lazyPage(() => import('./pages/admin/EmailTestPage.js'), 'EmailTestPage') },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
